import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./entry-middle-recruitment.css";
import { 
  Sprout, Rocket, Star, Monitor, Building2, HeartPulse, 
  ShoppingCart, HardHat, GraduationCap, Factory, Plane, 
  Target, CheckCircle2, Check, ArrowRight, Sparkles
} from "lucide-react";

const levels = [
  {
    id: "entry",
    label: "Entry Level",
    icon: "Sprout",
    exp: "0–2 Years",
    title: "Fresh Talent & Early Careers",
    desc: "We source ambitious graduates and early-career professionals who bring fresh energy, digital fluency, and growth potential to your organisation.",
    roles: ["Graduate Trainees", "Junior Analysts", "Associate Engineers", "Executive Assistants", "Sales Executives", "Content Writers", "Support Representatives"],
    features: [
      "Campus recruitment drives",
      "Graduate talent pipeline",
      "Aptitude & skill screening",
      "Rapid onboarding support",
      "Bulk hiring for large teams",
    ],
  },
  {
    id: "middle",
    label: "Mid Level",
    icon: "Rocket",
    exp: "3–10 Years",
    title: "Experienced Professionals",
    desc: "Targeting high-performing professionals ready for the next challenge — team leads, project managers, specialists, and department heads across functions.",
    roles: ["Team Leaders", "Project Managers", "Senior Analysts", "Product Managers", "Business Developers", "HR Business Partners", "Senior Engineers"],
    features: [
      "Specialist skill matching",
      "Leadership potential assessment",
      "Culture & values alignment",
      "Competitive benchmarking",
      "Cross-industry talent access",
    ],
  },
  {
    id: "senior",
    label: "Senior Level",
    icon: "Star",
    exp: "10+ Years",
    title: "Senior & Functional Leaders",
    desc: "Placing seasoned professionals into senior individual contributor and functional leadership roles requiring deep domain expertise and strategic thinking.",
    roles: ["Department Heads", "Principal Engineers", "Senior Managers", "Directors", "Functional VPs", "Practice Leads", "Regional Managers"],
    features: [
      "Targeted headhunting",
      "Competency-based interviews",
      "Leadership profiling",
      "Reference & background checks",
      "Long-term retention focus",
    ],
  },
];

const industries = [
  { icon: "Monitor", name: "IT & Software" },
  { icon: "Building2", name: "BFSI" },
  { icon: "HeartPulse", name: "Healthcare" },
  { icon: "ShoppingCart", name: "FMCG & Retail" },
  { icon: "HardHat", name: "Engineering" },
  { icon: "GraduationCap", name: "EdTech" },
  { icon: "Factory", name: "Manufacturing" },
  { icon: "Plane", name: "Logistics" },
];

const timelineSteps = [
  { num: "01", title: "Intake Call", time: "Day 1", desc: "Understand role, team culture & must-have qualifications" },
  { num: "02", title: "Sourcing", time: "Day 2–4", desc: "Multi-channel search: job boards, networks & database" },
  { num: "03", title: "Screening", time: "Day 4–6", desc: "Resume review, telephonic screening & skills assessment" },
  { num: "04", title: "Shortlist", time: "Day 7", desc: "Curated shortlist of 3–5 top candidates delivered" },
  { num: "05", title: "Interviews", time: "Day 8–12", desc: "Coordination of client interviews & candidate feedback" },
  { num: "06", title: "Closure", time: "Day 13–15", desc: "Offer management, negotiation & joining confirmation" },
];

const stats = [
  { num: "1200+", label: "Mid-Level Placements" },
  { num: "15", label: "Avg. Days to Hire" },
  { num: "94%", label: "Offer Acceptance Rate" },
  { num: "50+", label: "Active Client Mandates" },
];

const iconMap = {
  Sprout: <Sprout size={18} />,
  Rocket: <Rocket size={18} />,
  Star: <Star size={18} />,
  Monitor: <Monitor size={18} />,
  Building2: <Building2 size={18} />,
  HeartPulse: <HeartPulse size={18} />,
  ShoppingCart: <ShoppingCart size={18} />,
  HardHat: <HardHat size={18} />,
  GraduationCap: <GraduationCap size={18} />,
  Factory: <Factory size={18} />,
  Plane: <Plane size={18} />,
};

const iconLargeMap = {
  Sprout: <Sprout size={48} />,
  Rocket: <Rocket size={48} />,
  Star: <Star size={48} />,
};

export default function EntryMiddleRecruitment() {
  const revealRefs = useRef([]);
  const [activeLevel, setActiveLevel] = useState("entry");

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("em-shown"); }),
      { threshold: 0.1 }
    );
    revealRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const r = (el) => { if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el); };
  const activeLvl = levels.find((l) => l.id === activeLevel);

  return (
    <div className="em-page">

      {/* ── HERO ── */}
      <section className="em-hero">
        <div className="em-hero-bg-pattern" />
        <div className="em-hero-bg-gradient" />
        <div className="em-hero-inner">
          <div className="em-hero-left">
            <div className="em-badge">
              <span className="em-badge-dot" /> 
              Entry &amp; Mid-Level Recruitment
            </div>
            <h1>
              Find Talent That<br />
              <span className="em-hl">Grows</span> With Your<br />
              <span className="em-hl2">Business</span>
            </h1>
            <p className="em-hero-sub">Right talent. Right level. Right time.</p>
            <p className="em-hero-desc">
              From fresh graduates to seasoned professionals — EduITalent sources,
              screens, and delivers candidates who are aligned with your culture,
              goals, and growth trajectory.
            </p>
            <div className="em-hero-chips">
              {["Entry Level", "Mid Level", "Senior Professionals", "Bulk Hiring", "Niche Roles"].map((c, i) => (
                <span key={i} className="em-hero-chip">
                  <Sparkles size={10} className="em-hero-chip-icon" />
                  {c}
                </span>
              ))}
            </div>
            <div className="em-hero-btns">
              <Link to="/contact" className="em-btn-main">
                Start Hiring Now <ArrowRight size={16} />
              </Link>
              <Link to="/services/recruitment-services" className="em-btn-ghost">All Services ↗</Link>
            </div>
          </div>
          
          <div className="em-hero-right">
            <div className="em-dashboard-mock animate-float">
              <div className="em-dash-header">
                <div className="em-dash-logo">
                  <div className="em-dash-dot"></div>
                  EduITalent Talent Pool
                </div>
                <span className="em-dash-badge">Active Sourcing</span>
              </div>

              <div className="em-dash-stats">
                <div className="em-dstat-card blue">
                  <span>Hires Completed</span>
                  <strong>1,200+ Hires</strong>
                </div>
                <div className="em-dstat-card purple">
                  <span>Avg. Speed-to-Hire</span>
                  <strong>15 Days</strong>
                </div>
              </div>

              <div className="em-dash-profile-card">
                <div className="em-dp-header">
                  <div className="em-dp-avatar">QA</div>
                  <div className="em-dp-info">
                    <h4>Vetted Talents Feed</h4>
                    <span>Junior &amp; Mid Engineers</span>
                  </div>
                  <span className="em-dp-score">94% Match</span>
                </div>
                <div className="em-dp-skills">
                  <span className="em-dp-skill">React</span>
                  <span className="em-dp-skill">Node.js</span>
                  <span className="em-dp-skill">Python</span>
                  <span className="em-dp-skill">System Design</span>
                </div>
                <div className="em-dp-stages">
                  <div className="em-stage active">Source</div>
                  <div className="em-stage active">Screen</div>
                  <div className="em-stage active">Verify</div>
                  <div className="em-stage current">Offer</div>
                </div>
              </div>

              <span className="em-dash-list-title">Active Recruitment Mandates</span>
              <div className="em-dash-list">
                <div className="em-dash-list-item">
                  <div className="em-dli-icon blue">
                    <CheckCircle2 size={14} />
                  </div>
                  <div className="em-dli-info">
                    <strong>Software Engineer II (Mid)</strong>
                    <span>3 Placements Pending</span>
                  </div>
                </div>
                <div className="em-dash-list-item">
                  <div className="em-dli-icon purple">
                    <Target size={14} />
                  </div>
                  <div className="em-dli-info">
                    <strong>Graduate Trainee Drive</strong>
                    <span>45 Hires Dispatched</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── LEVEL SELECTOR ── */}
      <section className="em-levels-section">
        <div className="em-main-container">
          <div className="em-level-hdr em-reveal" ref={r}>
            <span className="em-eyebrow">Hiring for Every Stage</span>
            <h2>Select Your Hiring Level</h2>
            <p>We specialise in talent acquisition across all experience bands — with dedicated search teams per level</p>
          </div>

          <div className="em-level-tabs">
            {levels.map((lvl) => (
              <button
                key={lvl.id}
                className={`em-level-btn ${activeLevel === lvl.id ? "active" : ""}`}
                onClick={() => setActiveLevel(lvl.id)}
              >
                <span className="em-level-icon">{iconMap[lvl.icon]}</span> 
                <span>{lvl.label}</span>
                <span className="em-exp-badge">{lvl.exp}</span>
              </button>
            ))}
          </div>

          <div className="em-level-panel em-reveal" ref={r}>
            <div className="em-lp-left">
              <div className="em-lp-icon">
                {iconLargeMap[activeLvl.icon]}
              </div>
              <div className="em-lp-exp">{activeLvl.exp} Experience</div>
              <h3>{activeLvl.title}</h3>
              <p>{activeLvl.desc}</p>
              <ul className="em-flist">
                {activeLvl.features.map((f, i) => (
                  <li key={i}>
                    <Check size={12} className="em-check-icon" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="em-lp-right">
              <div className="em-lp-roles-title">Typical Roles We Fill</div>
              <div className="em-pills">
                {activeLvl.roles.map((role, i) => (
                  <span key={i} className="em-pill">
                    <span className="em-pill-dot" />
                    {role}
                  </span>
                ))}
              </div>
              <div className="em-lp-cta">
                <Link to="/contact" className="em-cta-btn">
                  Discuss This Requirement <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── INDUSTRIES ── */}
      <section className="em-ind-section">
        <div className="em-main-container">
          <div className="em-reveal" ref={r}>
            <span className="em-eyebrow">Sectors We Serve</span>
            <h2 className="em-ind-title">Expertise Across Every Industry</h2>
          </div>
          <div className="em-industries">
            {industries.map((ind, i) => (
              <div className="em-industry-tag em-reveal" key={i} ref={r} style={{ transitionDelay: `${i * 0.06}s` }}>
                <span className="em-ind-icon">{iconMap[ind.icon]}</span>
                <span>{ind.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS BAND ── */}
      <div className="em-stats-band em-reveal" ref={r}>
        <div className="em-main-container">
          <div className="em-stats-grid">
            {stats.map((s, i) => (
              <div className="em-stat" key={i}>
                <div className="em-stat-num">{s.num}</div>
                <div className="em-stat-lbl">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── TIMELINE ── */}
      <section className="em-timeline-section">
        <div className="em-main-container">
          <div className="em-section-header em-reveal" ref={r}>
            <span className="em-eyebrow">Our Speed-to-Hire Process</span>
            <h2>Hired in 15 Days or Less</h2>
            <p>A streamlined, transparent recruitment process that respects your time and delivers quality</p>
          </div>
          <div className="em-timeline em-reveal" ref={r}>
            <div className="em-tline">
              {timelineSteps.map((step, i) => (
                <div className="em-tstep" key={i}>
                  <div className="em-tstep-num">{step.num}</div>
                  <div className="em-tstep-date">{step.time}</div>
                  <div className="em-tstep-title">{step.title}</div>
                  <div className="em-tstep-desc">{step.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="em-cta-section">
        <div className="em-main-container">
          <div className="em-cta em-reveal" ref={r}>
            <div className="em-cta-content">
              <h3>Ready to Find Your Next Star Hire?</h3>
              <p>Connect with our talent specialists to discuss your hiring needs — no obligation, just great advice.</p>
            </div>
            <Link to="/contact" className="em-cta-btn">
              Start a Conversation <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
